import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  History, ChevronLeft, Building2, ChevronDown,
  CalendarRange, X,
} from "lucide-react";
import toast from "react-hot-toast";
import { format, addMonths, subMonths, startOfMonth, getDaysInMonth, getDay, isSameDay, isAfter, isBefore, parseISO } from "date-fns";
import { useAuth } from "../../context/AuthContext";
import { listKeyHistory, listMovies } from "../../api/movieKeys";
import { listBranches } from "../../api/branches";
import { unwrapList, errorMessage, formatDate, formatDateTime } from "../../lib/records";
import { Button } from "../../components/ui";

const TYPE_STYLE = {
  ISSUE:    { bg: "bg-blue-100",    text: "text-blue-800",    border: "border-blue-200",    dot: "bg-blue-400",    label: "Issued" },
  ALLOCATE: { bg: "bg-purple-100",  text: "text-purple-800",  border: "border-purple-200",  dot: "bg-purple-400",  label: "Allocated" },
  REVOKE:   { bg: "bg-red-100",     text: "text-red-800",     border: "border-red-200",     dot: "bg-red-400",     label: "Revoked" },
  RETURN:   { bg: "bg-gray-100",    text: "text-gray-700",    border: "border-gray-200",    dot: "bg-gray-400",    label: "Returned" },
  CONSUME:  { bg: "bg-amber-100",   text: "text-amber-800",   border: "border-amber-200",   dot: "bg-amber-400",   label: "Consumed" },
  RESTORE:  { bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-200", dot: "bg-emerald-400", label: "Restored" },
};

const TYPE_ORDER = ["ISSUE", "ALLOCATE", "REVOKE", "CONSUME", "RETURN", "RESTORE"];

// ---------------------------------------------------------------------------
// Single-calendar date range picker
// ---------------------------------------------------------------------------
function DateRangePicker({ from, to, onChange }) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [viewMonth, setViewMonth] = useState(() => {
    const base = from ? parseISO(from) : new Date();
    return startOfMonth(base);
  });
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function close(e) {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  function handleDayClick(dateStr) {
    if (!from || (from && to)) {
      onChange({ from: dateStr, to: "" });
    } else {
      const clickedDate = parseISO(dateStr);
      const fromDate = parseISO(from);
      if (isBefore(clickedDate, fromDate)) {
        onChange({ from: dateStr, to: from });
      } else {
        onChange({ from, to: dateStr });
        setOpen(false);
      }
    }
  }

  function buildDays() {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const total = getDaysInMonth(viewMonth);
    const firstDow = getDay(new Date(year, month, 1));
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= total; d++) {
      cells.push(new Date(year, month, d));
    }
    return cells;
  }

  const days = buildDays();
  const fromDate = from ? parseISO(from) : null;
  const toDate = to ? parseISO(to) : null;

  function dayClasses(date) {
    if (!date) return "";
    const ds = format(date, "yyyy-MM-dd");
    const isFrom = fromDate && isSameDay(date, fromDate);
    const isTo = toDate && isSameDay(date, toDate);
    const inRange = fromDate && toDate && isAfter(date, fromDate) && isBefore(date, toDate);
    const inHover = fromDate && !toDate && hovered && isAfter(date, fromDate) && isBefore(date, parseISO(hovered));

    if (isFrom || isTo) return "bg-emerald-500 text-white rounded-full";
    if (inRange || inHover) return "bg-emerald-100 text-emerald-800";
    return "hover:bg-gray-100 text-gray-700 rounded-full";
  }

  const label = from && to
    ? `${format(parseISO(from), "MMM d")} – ${format(parseISO(to), "MMM d, yyyy")}`
    : from
    ? `From ${format(parseISO(from), "MMM d")} …`
    : "Date range";

  return (
    <div className="relative" ref={popRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition bg-white ${
          open ? "border-emerald-400 ring-2 ring-emerald-500/20" : "border-gray-200 hover:border-gray-300"
        } ${from ? "text-gray-800 font-medium" : "text-gray-400"}`}
      >
        <CalendarRange size={14} className="text-gray-400 shrink-0" />
        {label}
        {from && (
          <span
            className="ml-1 text-gray-400 hover:text-gray-600"
            onClick={e => { e.stopPropagation(); onChange({ from: "", to: "" }); }}
          >
            <X size={12} />
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1.5 z-40 w-72 rounded-xl bg-white border border-gray-200 shadow-xl p-3">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => setViewMonth(m => subMonths(m, 1))}
              className="rounded p-1 hover:bg-gray-100 text-gray-500"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-semibold text-gray-800">
              {format(viewMonth, "MMMM yyyy")}
            </span>
            <button
              type="button"
              onClick={() => setViewMonth(m => addMonths(m, 1))}
              className="rounded p-1 hover:bg-gray-100 text-gray-500"
            >
              <ChevronDown size={16} className="-rotate-90" />
            </button>
          </div>

          {/* Day-of-week header */}
          <div className="grid grid-cols-7 mb-1">
            {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => (
              <div key={d} className="text-center text-[10px] font-semibold text-gray-400 py-1">{d}</div>
            ))}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7 gap-y-0.5">
            {days.map((date, idx) => {
              if (!date) return <div key={`blank-${idx}`} />;
              const dateStr = format(date, "yyyy-MM-dd");
              return (
                <button
                  key={dateStr}
                  type="button"
                  onClick={() => handleDayClick(dateStr)}
                  onMouseEnter={() => fromDate && !toDate && setHovered(dateStr)}
                  onMouseLeave={() => setHovered(null)}
                  className={`h-8 w-full text-[13px] font-medium transition ${dayClasses(date)}`}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          {/* Hint */}
          <p className="mt-2 text-center text-[11px] text-gray-400">
            {!from ? "Click to set start date" : !to ? "Click to set end date" : "Click to reset"}
          </p>
        </div>
      )}
    </div>
  );
}

const TIER_BADGE = {
  CHAIN:    "bg-emerald-100 text-emerald-700",
  BRANCH:   "bg-blue-100 text-blue-700",
  EMPLOYEE: "bg-purple-100 text-purple-700",
};

function TierBadge({ tier }) {
  if (!tier) return null;
  return (
    <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${TIER_BADGE[tier] ?? "bg-gray-100 text-gray-500"}`}>
      {tier}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Accordion row
// ---------------------------------------------------------------------------
function TypeAccordion({ type, items }) {
  const [open, setOpen] = useState(false);
  const style = TYPE_STYLE[type] ?? { bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-200", dot: "bg-gray-300", label: type };
  const totalKeys = items.reduce((acc, mv) => acc + (mv.keys ?? 1), 0);

  return (
    <div className={`rounded-xl border ${style.border} overflow-hidden`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between px-4 py-3 ${style.bg} transition hover:opacity-90`}
      >
        <div className="flex items-center gap-3">
          <span className={`h-2.5 w-2.5 rounded-full ${style.dot}`} />
          <span className={`text-sm font-semibold ${style.text}`}>{style.label}</span>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full bg-white/60 ${style.text}`}>
            {items.length} movement{items.length !== 1 ? "s" : ""} · {totalKeys} key{totalKeys !== 1 ? "s" : ""}
          </span>
        </div>
        <ChevronDown
          size={16}
          className={`${style.text} transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="divide-y divide-gray-100 bg-white">
          {items.map(mv => {
            const movieTitle = mv.movie?.title ?? mv.movieTitle ?? "—";
            const hasTo = mv.toHolderName;
            return (
              <div key={mv._id} className="px-4 py-3.5 hover:bg-gray-50/60 transition">
                {/* Top row: movie + ref + time */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <span className="text-sm font-semibold text-gray-900 truncate">{movieTitle}</span>
                    {mv.movementId && (
                      <span className="text-[10px] font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                        {mv.movementId}
                      </span>
                    )}
                    {mv.isReversed && (
                      <span className="text-[10px] font-semibold bg-red-100 text-red-600 px-1.5 py-0.5 rounded">
                        REVERSED
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-400 shrink-0 whitespace-nowrap">
                    {formatDateTime(mv.recordedAt ?? mv.createdAt)}
                  </span>
                </div>

                {/* From → To flow */}
                <div className="flex items-center gap-2 flex-wrap text-sm">
                  {/* From holder */}
                  <div className="flex items-center gap-1.5">
                    <TierBadge tier={mv.fromTier} />
                    <span className="font-medium text-gray-800">{mv.fromHolderName ?? "—"}</span>
                    <span className="text-xs font-semibold tabular-nums text-red-500">
                      −{mv.keys ?? 1}
                    </span>
                  </div>

                  {/* Arrow + key label */}
                  <div className="flex items-center gap-1">
                    <span className="text-gray-300 text-xs">──</span>
                    <span className={`text-xs font-bold tabular-nums px-1.5 py-0.5 rounded-full ${style.bg} ${style.text}`}>
                      {mv.keys ?? 1} key{(mv.keys ?? 1) !== 1 ? "s" : ""}
                    </span>
                    <span className="text-gray-300 text-xs">──▶</span>
                  </div>

                  {/* To holder */}
                  {hasTo ? (
                    <div className="flex items-center gap-1.5">
                      <TierBadge tier={mv.toTier} />
                      <span className="font-medium text-gray-800">{mv.toHolderName}</span>
                      <span className="text-xs font-semibold tabular-nums text-emerald-600">
                        +{mv.keys ?? 1}
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400 italic">consumed</span>
                  )}
                </div>

                {/* Show details */}
                {mv.show && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 flex-wrap">
                    <span className="font-semibold">Show:</span>
                    {mv.show.date && <span>{formatDate(mv.show.date)}</span>}
                    {mv.show.time && <span>· {mv.show.time}</span>}
                    {mv.show.screen && <span>· {mv.show.screen}</span>}
                  </div>
                )}

                {/* Note / ref / acted-by */}
                {(mv.note || mv.clientRef || mv.byName) && (
                  <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-400">
                    {mv.byName && <span>By: <span className="text-gray-600 font-medium">{mv.byName}</span></span>}
                    {mv.note && <span className="italic">{mv.note}</span>}
                    {mv.clientRef && <span className="font-mono">Ref: {mv.clientRef}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function MovieKeyHistory() {
  const navigate = useNavigate();
  const { partner } = useAuth();
  const isChain = partner?.kind === "CHAIN";
  const [searchParams] = useSearchParams();
  const presetMovie = searchParams.get("movie") ?? "";

  const [movements, setMovements] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [movies, setMovies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [page, setPage] = useState(1);

  const [movieFilter, setMovieFilter] = useState(presetMovie);
  const [typeFilter, setTypeFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  const loadMovies = useCallback(async () => {
    try {
      const res = await listMovies({ limit: 100 });
      setMovies(unwrapList(res).items);
    } catch { /* non-fatal */ }
  }, []);

  const loadBranches = useCallback(async () => {
    if (!isChain) return;
    try {
      const res = await listBranches({ limit: 100 });
      setBranches(unwrapList(res).items);
    } catch { /* non-fatal */ }
  }, [isChain]);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await listKeyHistory({
        page: p,
        limit: 50,
        ...(movieFilter       ? { movie:   movieFilter       } : {}),
        ...(typeFilter        ? { type:    typeFilter        } : {}),
        ...(branchFilter      ? { partner: branchFilter      } : {}),
        ...(dateRange.from    ? { from:    dateRange.from    } : {}),
        ...(dateRange.to      ? { to:      dateRange.to      } : {}),
      });
      const { items, pagination: pg } = unwrapList(res);
      setMovements(items);
      setPagination(pg);
      setPage(p);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [movieFilter, typeFilter, branchFilter, dateRange]);

  useEffect(() => { loadMovies(); loadBranches(); }, [loadMovies, loadBranches]);
  useEffect(() => { load(1); }, [load]);

  function clearFilters() {
    setMovieFilter(presetMovie);
    setTypeFilter("");
    setBranchFilter("");
    setDateRange({ from: "", to: "" });
  }

  const hasFilters = movieFilter !== presetMovie || typeFilter || branchFilter || dateRange.from;

  // Group movements by type for accordion display
  const grouped = TYPE_ORDER.reduce((acc, t) => {
    const items = movements.filter(mv => mv.type === t);
    if (items.length > 0) acc[t] = items;
    return acc;
  }, {});
  // Types not in TYPE_ORDER (rare/future)
  movements.forEach(mv => {
    if (!TYPE_ORDER.includes(mv.type) && mv.type) {
      if (!grouped[mv.type]) grouped[mv.type] = [];
      grouped[mv.type].push(mv);
    }
  });

  return (
    <div className="space-y-4 p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/cinema")}
          className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
        >
          <ChevronLeft size={18} />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <History size={20} className="text-emerald-600" />
            Key Movement History
          </h1>
          <p className="text-sm text-gray-500">All issuances, allocations, consumptions, and reversals</p>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl bg-white border border-gray-100 shadow-sm p-3 flex flex-wrap items-center gap-2">
        {/* Branch filter first */}
        {isChain && branches.length > 0 && (
          <div className="flex items-center gap-1.5">
            <Building2 size={14} className="text-gray-400 shrink-0" />
            <select
              value={branchFilter}
              onChange={e => setBranchFilter(e.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 bg-white"
            >
              <option value="">All branches</option>
              {branches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
            </select>
          </div>
        )}

        <select
          value={movieFilter}
          onChange={e => setMovieFilter(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 bg-white"
        >
          <option value="">All films</option>
          {movies.map(m => <option key={m._id} value={m._id}>{m.title}</option>)}
        </select>

        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 bg-white"
        >
          <option value="">All types</option>
          {Object.entries(TYPE_STYLE).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>

        <DateRangePicker
          from={dateRange.from}
          to={dateRange.to}
          onChange={setDateRange}
        />

        {hasFilters && (
          <button
            onClick={clearFilters}
            className="rounded-lg px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 transition flex items-center gap-1"
          >
            <X size={12} /> Clear all
          </button>
        )}
      </div>

      {/* Accordion groups */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => <div key={i} className="h-12 rounded-xl bg-gray-100 animate-pulse" />)}
        </div>
      ) : movements.length === 0 ? (
        <div className="rounded-xl bg-white border border-gray-100 shadow-sm p-12 text-center text-gray-400">
          <History size={28} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm">No movements found</p>
          {hasFilters && (
            <button onClick={clearFilters} className="mt-2 text-xs text-emerald-600 hover:underline">
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {Object.entries(grouped).map(([type, items]) => (
            <TypeAccordion key={type} type={type} items={items} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && (pagination.pages ?? 1) > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            {pagination.total} total · page {page} of {pagination.pages}
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" disabled={page <= 1} onClick={() => load(page - 1)} className="text-sm border border-gray-200">
              Previous
            </Button>
            <Button variant="ghost" disabled={page >= pagination.pages} onClick={() => load(page + 1)} className="text-sm border border-gray-200">
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
